import { Cloud, Fingerprint, KeyRound, ShieldCheck } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Button, Callout, ErrorState, ExplainPanel } from "@/design";
import { ApiError, api, idem, unwrap, type LoginResponse, type PasskeyChallenge } from "@/lib/api";
import { createCredential, PasskeyCancelled, passkeysSupported } from "@/lib/passkey";
import { StepHeading } from "./OnboardingFrame";
import type { SignupDraft } from "./SignupFlow";

/** Challenges last five minutes; ask for a fresh one a little before that. */
const CHALLENGE_TTL_MS = 4 * 60_000;

export function PasskeyStep({
  draft,
  renew,
  onDone,
  onBadCode,
  joining,
}: {
  draft: SignupDraft;
  renew: () => Promise<PasskeyChallenge>;
  onDone: (res: LoginResponse) => Promise<void>;
  onBadCode: () => void;
  /** Finishing a workspace invitation: the account joins that workspace next. */
  joining: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [cancelled, setCancelled] = useState(false);
  const supported = passkeysSupported();

  async function create() {
    setBusy(true);
    setError(null);
    setCancelled(false);
    try {
      let ch = draft.challenge;
      if (!ch || Date.now() - draft.challengeAt > CHALLENGE_TTL_MS) {
        try {
          ch = await renew();
        } catch (e) {
          // The code was single-use or has expired: go back and get a new one.
          if (e instanceof ApiError && e.status >= 400 && e.status < 500) return onBadCode();
          throw e;
        }
      }
      const credential = await createCredential(ch.public_key_creation);
      const res = await unwrap(api.POST("/v1/auth/passkeys/register/finish", { params: { header: idem() }, body: { challenge_id: ch.challenge_id, credential } }));
      await onDone(res);
    } catch (e) {
      if (e instanceof PasskeyCancelled) setCancelled(true);
      else setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <StepHeading eyebrow="Email confirmed" title="Now, a passkey instead of a password">
        A passkey lets you sign in with your fingerprint, your face or your screen lock. It takes about five seconds to set up.
      </StepHeading>

      <ul className="mt-7 divide-y divide-border rounded-xl border border-border bg-surface">
        <Reason icon={<KeyRound />} title="Nothing to remember">
          No password to invent, reuse or forget.
        </Reason>
        <Reason icon={<ShieldCheck />} title="It can't be phished">
          It only works on Guv's real website, so a fake login page gets nothing.
        </Reason>
        <Reason icon={<Cloud />} title="It follows you">
          Saved by Apple, Google, Microsoft or your password manager, so your other devices can use it too.
        </Reason>
      </ul>

      {!supported && (
        <Callout tone="warn" className="mt-5" title="This browser can't make passkeys">
          Open this page in an up-to-date Safari, Chrome, Edge or Firefox, or on your phone. Your email is already confirmed, so you'll pick up from here.
        </Callout>
      )}

      <Button variant="primary" size="lg" className="mt-6 w-full" icon={<Fingerprint />} loading={busy} disabled={!supported} onClick={() => void create()}>
        {joining ? "Create my passkey and join the team" : "Create my passkey"}
      </Button>
      <p className="mt-2.5 text-center text-xs text-subtle">Your device will ask you to confirm. Guv never sees your fingerprint or face.</p>

      {cancelled && (
        <Callout tone="info" className="mt-5">
          No passkey was saved, and that's fine. Press the button again when you're ready. If your device offered a phone or security key, you can use those too.
        </Callout>
      )}
      {error != null && <ErrorState error={error} className="mt-5" />}

      <div className="mt-6 flex justify-center">
        <ExplainPanel title="What is a passkey?" trigger="link" triggerLabel="How do passkeys work?">
          <p>A passkey is a pair of keys made by your device. The private half stays on your device or in your password manager, locked behind your fingerprint, face or PIN. Guv only ever keeps the public half, which is useless to anyone who steals it.</p>
          <p className="mt-2">When you sign in, your device proves it has the private half. Because the passkey is tied to Guv's web address, a lookalike site can't trick it.</p>
          <p className="mt-2">You can add more passkeys later, for example on your phone, in your account settings.</p>
        </ExplainPanel>
      </div>
    </div>
  );
}

function Reason({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <li className="flex gap-3.5 px-4 py-3.5">
      <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent-text [&_svg]:size-4" aria-hidden="true">
        {icon}
      </span>
      <div>
        <div className="text-sm font-semibold text-text">{title}</div>
        <p className="mt-0.5 text-sm text-muted">{children}</p>
      </div>
    </li>
  );
}
